import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import TikTokEmbed from '@/components/TikTokEmbed';
import FacebookEmbed from '@/components/FacebookEmbed';
import { VIDEOS as DATA_VIDEOS, SOCIAL_LINKS } from '@/data/socials';
import {
  Facebook,
  ExternalLink,
  Plus,
  Trash2,
  Copy,
  ShieldCheck,
  Music2,
  LayoutGrid,
  Info,
} from 'lucide-react';

const LS_KEY = 'drugawarenessusa.socials.v1';

// Auto-detect platform from URL
function detectPlatform(url) {
  if (!url) return null;
  const u = url.toLowerCase();
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
  return null;
}

function readLocalVideos() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function writeLocalVideos(list) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {}
}

function VideoCard({ video }) {
  const platformLabel = video.platform === 'tiktok' ? 'TikTok' : 'Facebook';
  const PlatformIcon = video.platform === 'tiktok' ? Music2 : Facebook;

  return (
    <article className="bg-white rounded-lg overflow-hidden shadow-sm border border-slate-200 hover:shadow-md transition-all flex flex-col">
      <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#0b2545]">
          <PlatformIcon className="h-4 w-4 text-amber-500" />
          <span className="uppercase tracking-widest">{platformLabel}</span>
        </div>
        <a
          href={video.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-slate-500 hover:text-amber-600 inline-flex items-center gap-1 transition-colors"
        >
          View original <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="p-3 flex-1 flex flex-col">
        <div className="flex-1">
          {video.platform === 'tiktok' ? (
            <TikTokEmbed url={video.url} title={video.title} />
          ) : (
            <FacebookEmbed url={video.url} title={video.title} />
          )}
        </div>
        {(video.title || video.date) && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            {video.title && (
              <div className="font-serif font-semibold text-[#0b2545] text-sm leading-snug">
                {video.title}
              </div>
            )}
            {video.date && (
              <div className="text-xs text-slate-500 mt-1">{video.date}</div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function AdminPanel({ onChange, localVideos, setLocalVideos }) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');

  const addVideo = () => {
    setError('');
    const p = detectPlatform(url);
    if (!p) {
      setError('Please paste a valid TikTok or Facebook video URL.');
      return;
    }
    const next = [{ platform: p, url: url.trim(), title: title.trim() || undefined }, ...localVideos];
    setLocalVideos(next);
    writeLocalVideos(next);
    onChange?.();
    setUrl('');
    setTitle('');
    toast({
      title: 'Video added to preview',
      description: 'Visible to you locally. Use "Copy snippet" to add it permanently for all visitors.',
    });
  };

  const remove = (idx) => {
    const next = localVideos.filter((_, i) => i !== idx);
    setLocalVideos(next);
    writeLocalVideos(next);
    onChange?.();
  };

  const copyAll = async () => {
    if (!localVideos.length) {
      toast({ title: 'Nothing to copy', description: 'Add at least one video first.' });
      return;
    }
    const snippet = localVideos
      .map(
        (v) =>
          `  {\n    platform: '${v.platform}',\n    url: '${v.url}',\n${
            v.title ? `    title: ${JSON.stringify(v.title)},\n` : ''
          }  },`
      )
      .join('\n');
    const text = `// Paste these into VIDEOS in src/data/socials.js\n${snippet}`;
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Snippet copied',
        description: 'Paste it into src/data/socials.js and redeploy to make these live for everyone.',
      });
    } catch {
      toast({ title: 'Copy failed', description: 'Please copy manually from the preview below.' });
    }
  };

  return (
    <div className="bg-white p-6 md:p-8 rounded-lg border border-slate-200 shadow-sm">
      <div className="flex items-start gap-4 mb-6">
        <div className="h-11 w-11 rounded-md bg-[#0b2545] text-amber-400 flex items-center justify-center flex-shrink-0">
          <Plus className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-serif text-xl font-bold text-[#0b2545]">Add a video</h2>
          <p className="text-sm text-slate-600 mt-1">
            Paste any TikTok or Facebook video URL. We detect the platform, embed it using the
            official method, and preview it below.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_240px_auto] gap-3 items-end">
        <div>
          <Label htmlFor="soc-url" className="text-slate-700">Video URL</Label>
          <Input
            id="soc-url"
            placeholder="https://www.tiktok.com/@user/video/... or https://www.facebook.com/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="mt-2"
          />
        </div>
        <div>
          <Label htmlFor="soc-title" className="text-slate-700">Title (optional)</Label>
          <Input
            id="soc-title"
            placeholder="Short caption"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-2"
          />
        </div>
        <Button onClick={addVideo} className="bg-amber-500 hover:bg-amber-400 text-[#0b2545] font-semibold">
          <Plus className="mr-1 h-4 w-4" /> Add
        </Button>
      </div>
      {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

      {localVideos.length > 0 && (
        <>
          <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-slate-600">
              <strong>{localVideos.length}</strong> preview video{localVideos.length !== 1 ? 's' : ''} stored locally.
            </div>
            <Button onClick={copyAll} variant="outline" className="border-[#0b2545] text-[#0b2545] hover:bg-[#0b2545] hover:text-white">
              <Copy className="mr-2 h-4 w-4" /> Copy snippet for socials.js
            </Button>
          </div>

          <ul className="mt-4 space-y-2">
            {localVideos.map((v, i) => (
              <li key={i} className="flex items-center gap-3 p-3 rounded-md bg-slate-50 border border-slate-200 text-sm">
                <span className="text-xs font-bold uppercase tracking-widest text-amber-600 w-16 flex-shrink-0">
                  {v.platform}
                </span>
                <span className="flex-1 truncate text-slate-700">{v.title || v.url}</span>
                <button
                  onClick={() => remove(i)}
                  className="text-slate-400 hover:text-red-600 transition-colors"
                  aria-label="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-3">
        <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-slate-700 leading-relaxed">
          Videos added here are stored in your browser only — great for previewing. To publish them
          to all visitors, click <strong>Copy snippet</strong> and paste into
          <code className="mx-1 px-1 py-0.5 bg-white rounded border text-[11px]">src/data/socials.js</code>
          then redeploy.
        </p>
      </div>
    </div>
  );
}

export default function Socials() {
  const [localVideos, setLocalVideos] = useState([]);
  const [tick, setTick] = useState(0);
  const [showAdmin, setShowAdmin] = useState(false);

  useEffect(() => {
    setLocalVideos(readLocalVideos());
    if (typeof window !== 'undefined' && window.location.hash === '#admin') {
      setShowAdmin(true);
    }
  }, []);

  const merged = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const v of [...localVideos, ...DATA_VIDEOS]) {
      if (!v?.url || seen.has(v.url)) continue;
      seen.add(v.url);
      out.push(v);
    }
    return out;
  }, [localVideos, tick]);

  const tiktokCount = merged.filter((v) => v.platform === 'tiktok').length;
  const fbCount = merged.filter((v) => v.platform === 'facebook').length;

  return (
    <div>
      <SEO
        title="Socials"
        description="Explore educational drug-awareness videos featuring Lieutenant Joe Kress and the Single Handed program on TikTok and Facebook."
        path="/socials"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0b2545] via-[#123a6b] to-[#0b2545] text-white">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              'radial-gradient(circle at 15% 20%, #f59e0b 0%, transparent 40%), radial-gradient(circle at 85% 80%, #f59e0b 0%, transparent 40%)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/15 border border-amber-300/30 text-amber-300 text-xs font-semibold uppercase tracking-widest mb-5">
            <LayoutGrid className="h-3.5 w-3.5" /> Follow the Mission
          </div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
            SOCIALS
          </h1>
          <p className="mt-5 text-lg md:text-xl text-slate-200 leading-relaxed max-w-2xl">
            Explore educational drug-awareness videos featuring Lieutenant Joe Kress and the
            Single Handed program.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {SOCIAL_LINKS.facebook && (
              <Button asChild size="lg" className="bg-[#1877F2] hover:bg-[#166fe0] text-white font-semibold">
                <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer">
                  <Facebook className="mr-2 h-4 w-4" /> Follow on Facebook
                </a>
              </Button>
            )}
            {SOCIAL_LINKS.tiktok && (
              <Button asChild size="lg" className="bg-black hover:bg-slate-900 text-white font-semibold">
                <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer">
                  <Music2 className="mr-2 h-4 w-4" /> Follow on TikTok
                </a>
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <Stat label="Facebook Videos" value={fbCount} icon={<Facebook className="h-5 w-5" />} />
          <Stat label="TikTok Videos" value={tiktokCount} icon={<Music2 className="h-5 w-5" />} />
          <Stat label="Total Available" value={merged.length} icon={<LayoutGrid className="h-5 w-5" />} />
          <Stat label="Official Embeds" value="100%" icon={<ShieldCheck className="h-5 w-5" />} />
        </div>
      </section>

      {/* Video grid */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        {merged.length === 0 ? (
          <EmptyState onOpenAdmin={() => setShowAdmin(true)} />
        ) : (
          <>
            <div className="mb-8">
              <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-2">
                Latest Content
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0b2545]">
                Videos & Reels
              </h2>
              <div className="mt-3 h-1 w-16 bg-amber-400" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {merged.map((v, i) => (
                <VideoCard key={`${v.url}-${i}`} video={v} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Admin toggle + panel */}
      <section className="max-w-4xl mx-auto px-4 pb-20">
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => setShowAdmin((s) => !s)}
            className="text-sm text-slate-500 hover:text-amber-600 transition-colors underline"
          >
            {showAdmin ? 'Hide admin' : 'Show admin (add a video)'}
          </button>
        </div>
        {showAdmin && (
          <AdminPanel
            onChange={() => setTick((t) => t + 1)}
            localVideos={localVideos}
            setLocalVideos={setLocalVideos}
          />
        )}
      </section>

      {/* CTA */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-gradient-to-br from-[#0b2545] to-[#123a6b] rounded-lg p-8 md:p-12 text-white text-center">
            <h2 className="font-serif text-2xl md:text-3xl font-bold mb-3">
              Bring the message to your community.
            </h2>
            <p className="text-slate-300 leading-relaxed mb-6 max-w-2xl mx-auto">
              Follow along on social, share the videos, or reach out to schedule a Drug &amp; Alcohol
              Awareness presentation.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button asChild className="bg-amber-500 hover:bg-amber-400 text-[#0b2545] font-semibold">
                <Link to="/contact">Contact Us</Link>
              </Button>
              <Button asChild variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0b2545]">
                <Link to="/single-handed">Listen to Single Handed</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div>
      <div className="inline-flex items-center gap-2 text-amber-600">
        {icon}
        <span className="text-xs uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className="font-serif text-3xl font-bold text-[#0b2545] mt-1">{value}</div>
    </div>
  );
}

function EmptyState({ onOpenAdmin }) {
  return (
    <div className="text-center py-16 px-4 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
      <LayoutGrid className="h-10 w-10 text-slate-400 mx-auto mb-4" />
      <h3 className="font-serif text-xl font-bold text-[#0b2545] mb-2">No videos yet</h3>
      <p className="text-slate-600 max-w-md mx-auto mb-6">
        Add the first TikTok or Facebook video URL from Joe Kress using the admin panel below.
      </p>
      <Button onClick={onOpenAdmin} className="bg-[#0b2545] hover:bg-[#123a6b] text-white">
        <Plus className="mr-2 h-4 w-4" /> Add a video
      </Button>
    </div>
  );
}
